/**
 * Porter Stemmer algorithm for English
 * Reduces words to their morphological root (stem)
 * Used by highlightKeywords to match plural/morphological variants
 *
 * Examples: samples→sampl, sample→sampl, parties→parti, party→parti,
 *           texts→text, running→run, generalization→gener
 *
 * Reference: https://tartarus.org/martin/PorterStemmer/
 */
export function porterStem(word) {
    if (!word || word.length < 3) return (word || '').toLowerCase();

    let stem = word.toLowerCase();

    function isConsonant(str, i) {
        switch (str[i]) {
            case 'a': case 'e': case 'i': case 'o': case 'u': return false;
            case 'y': return i === 0 || !isConsonant(str, i - 1);
            default: return true;
        }
    }

    function measure(str) {
        let n = 0, i = 0;
        const len = str.length;
        if (!len) return 0;
        while (i < len && !isConsonant(str, i)) i++;
        while (i < len) {
            while (i < len && isConsonant(str, i)) i++;
            if (i >= len) break;
            n++;
            while (i < len && !isConsonant(str, i)) i++;
        }
        return n;
    }

    function hasVowel(str) {
        for (let i = 0; i < str.length; i++) {
            if (!isConsonant(str, i)) return true;
        }
        return false;
    }

    function endsDoubleConsonant(str) {
        const len = str.length;
        return len >= 2 && str[len - 1] === str[len - 2] && isConsonant(str, len - 1);
    }

    function cvc(str) {
        const len = str.length;
        if (len < 3) return false;
        if (!isConsonant(str, len - 1) || isConsonant(str, len - 2) || !isConsonant(str, len - 3)) return false;
        const ch = str[len - 1];
        return ch !== 'w' && ch !== 'x' && ch !== 'y';
    }

    // Step 1a
    if (stem.endsWith('sses')) stem = stem.slice(0, -2);
    else if (stem.endsWith('ies')) stem = stem.slice(0, -2);
    else if (!stem.endsWith('ss') && stem.endsWith('s')) stem = stem.slice(0, -1);

    // Step 1b
    let step1b = false;
    if (stem.endsWith('eed')) {
        if (measure(stem.slice(0, -3)) > 0) stem = stem.slice(0, -1);
    } else if (stem.endsWith('ed')) {
        const base = stem.slice(0, -2);
        if (hasVowel(base)) { stem = base; step1b = true; }
    } else if (stem.endsWith('ing')) {
        const base = stem.slice(0, -3);
        if (hasVowel(base)) { stem = base; step1b = true; }
    }

    if (step1b) {
        if (stem.endsWith('at') || stem.endsWith('bl') || stem.endsWith('iz')) {
            stem += 'e';
        } else if (endsDoubleConsonant(stem) && !stem.endsWith('l') && !stem.endsWith('s') && !stem.endsWith('z')) {
            stem = stem.slice(0, -1);
        } else if (measure(stem) === 1 && cvc(stem)) {
            stem += 'e';
        }
    }

    // Step 1c
    if (stem.endsWith('y') && hasVowel(stem.slice(0, -1))) {
        stem = stem.slice(0, -1) + 'i';
    }

    // Step 2
    const step2 = [
        ['ational', 'ate'], ['tional', 'tion'], ['enci', 'ence'], ['anci', 'ance'],
        ['izer', 'ize'], ['abli', 'able'], ['alli', 'al'], ['entli', 'ent'],
        ['eli', 'e'], ['ousli', 'ous'], ['ization', 'ize'], ['ation', 'ate'],
        ['ator', 'ate'], ['alism', 'al'], ['iveness', 'ive'], ['fulness', 'ful'],
        ['ousness', 'ous'], ['aliti', 'al'], ['iviti', 'ive'], ['biliti', 'ble']
    ];
    for (const [suffix, repl] of step2) {
        if (stem.endsWith(suffix)) {
            const base = stem.slice(0, -suffix.length);
            if (measure(base) > 0) stem = base + repl;
            break;
        }
    }

    // Step 3
    const step3 = [
        ['icate', 'ic'], ['ative', ''], ['alize', 'al'],
        ['iciti', 'ic'], ['ical', 'ic'], ['ful', ''], ['ness', '']
    ];
    for (const [suffix, repl] of step3) {
        if (stem.endsWith(suffix)) {
            const base = stem.slice(0, -suffix.length);
            if (measure(base) > 0) stem = base + repl;
            break;
        }
    }

    // Step 4
    const step4 = [
        'al', 'ance', 'ence', 'er', 'ic', 'able', 'ible', 'ant',
        'ement', 'ment', 'ent', 'ion', 'ou', 'ism', 'ate', 'iti',
        'ous', 'ive', 'ize'
    ];
    for (const suffix of step4) {
        if (stem.endsWith(suffix)) {
            const base = stem.slice(0, -suffix.length);
            if (suffix === 'ion') {
                if (measure(base) > 1 && (base.endsWith('s') || base.endsWith('t'))) stem = base;
            } else if (measure(base) > 1) {
                stem = base;
            }
            break;
        }
    }

    // Step 5a
    if (stem.endsWith('e')) {
        const base = stem.slice(0, -1);
        if (measure(base) > 1 || (measure(base) === 1 && !cvc(base))) stem = base;
    }

    // Step 5b
    if (measure(stem) > 1 && endsDoubleConsonant(stem) && stem.endsWith('l')) {
        stem = stem.slice(0, -1);
    }

    return stem;
}
